/**
 * Personas: quién le escribe a un cliente. Ranking de las más activas, con su estado (spam,
 * conflictiva, reclamo abierto, vip, influencer, fan, recurrente, nueva, ocasional) y un puntaje
 * de 0 a 100. Cada persona se abre con su ficha: historial completo, etiquetas y notas del
 * equipo (las mismas del inbox). En los clientes de BOT_PROFILE_CLIENTS el bot recibe este perfil
 * para decidir cómo responder y cuándo escalar.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { X } from "lucide-react";
import { usePeople, usePerson, type PersonProfile, type UserState } from "@/hooks/use-people";
import { useContact, useUpdateContact, SUGGESTED_TAGS } from "@/hooks/use-contact";
import { formatArrivalAge } from "@/lib/format";
import { cn } from "@/lib/cn";
import { REASON_LABEL } from "./monitoring-stats";

export const STATE_LABEL: Record<UserState, string> = {
  spam: "Spam",
  conflictive: "Conflictiva",
  open_claim: "Reclamo abierto",
  vip: "VIP",
  influencer: "Influencer",
  fan: "Fan",
  recurrent: "Recurrente",
  new: "Nueva",
  occasional: "Ocasional",
};

const STATE_CLASS: Record<UserState, string> = {
  spam: "bg-surface-2 text-text-muted border-default",
  conflictive: "bg-error-bg text-error-text border-error-border",
  open_claim: "bg-warning-bg text-warning-text border-warning-border",
  vip: "bg-magenta-soft-bg text-text-primary border-magenta",
  influencer: "bg-magenta-soft-bg text-text-primary border-magenta",
  fan: "bg-success-bg text-success-text border-success-border",
  recurrent: "bg-state-bot-bg text-state-bot-text border-state-bot-border",
  new: "bg-info-bg text-info-text border-info-border",
  occasional: "bg-surface-2 text-text-secondary border-default",
};

const ORDER: UserState[] = ["conflictive", "open_claim", "spam", "vip", "influencer", "fan", "recurrent", "new", "occasional"];

function StateBadge({ s }: { s: UserState }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-sans font-medium", STATE_CLASS[s])}>
      {STATE_LABEL[s]}
    </span>
  );
}

function Score({ n }: { n: number }) {
  const color = n >= 65 ? "var(--success-text)" : n >= 40 ? "var(--text-secondary, #888)" : "var(--error-text)";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-12 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <span className="block h-full rounded-full" style={{ width: `${n}%`, background: color }} />
      </span>
      <span className="font-mono text-2xs tabular-nums text-text-muted">{n}</span>
    </span>
  );
}

function Detail({ clientSlug, userId, days, onClose }: { clientSlug: string; userId: string; days: number; onClose: () => void }) {
  const { data } = usePerson(clientSlug, userId, days);
  const { data: contact } = useContact({ clientSlug, externalUserId: userId });
  const update = useUpdateContact();
  const [notes, setNotes] = useState<string | null>(null);
  const p = data?.profile;
  const tags = contact?.tags ?? [];
  const toggleTag = (t: string) =>
    update.mutate({
      key: { clientSlug, externalUserId: userId },
      tags: tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t],
    });

  return (
    <aside className="rounded-xl border border-default bg-surface-1 overflow-hidden flex flex-col max-h-[calc(100vh-140px)] sticky top-4">
      <header className="px-5 py-4 border-b border-subtle flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-sans text-base font-semibold text-text-primary truncate">@{p?.username ?? userId}</div>
          {p && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {p.flags.map((f) => (
                <StateBadge key={f} s={f} />
              ))}
              <Score n={p.score} />
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" aria-label="Cerrar">
          <X size={16} />
        </button>
      </header>
      {!p ? (
        <div className="p-5 text-sm text-text-muted">Cargando…</div>
      ) : (
        <div className="overflow-y-auto">
          <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-subtle">
            {[
              ["Mensajes", p.messages],
              ["Días activos", p.activeDays],
              ["Escalados", p.outcomes.waiting],
              ["Contestados", p.outcomes.answeredByBot + p.outcomes.answeredByHuman],
              ["Quejas", p.complaints],
              ["Menciones", p.mentions],
            ].map(([l, v]) => (
              <div key={l as string}>
                <div className="font-sans text-lg font-semibold tabular-nums text-text-primary">{v}</div>
                <div className="font-sans text-2xs text-text-muted">{l}</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-b border-subtle font-sans text-xs text-text-muted space-y-1">
            <div>
              {p.platforms.join(" · ")} · desde {p.firstSeen.slice(0, 10)} · último {formatArrivalAge(p.lastSeen)}
            </div>
            {Object.keys(p.reasons).length > 0 && (
              <div>
                Escalado por:{" "}
                {Object.entries(p.reasons)
                  .map(([k, n]) => `${REASON_LABEL[k] ?? k} ${n}`)
                  .join(", ")}
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-b border-subtle space-y-2">
            <div className="font-sans text-xs font-medium text-text-secondary">Etiquetas del equipo</div>
            <div className="flex flex-wrap gap-1.5">
              {[...new Set([...SUGGESTED_TAGS, "conflictivo", ...tags])].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-2xs font-sans font-medium transition-colors",
                    tags.includes(t)
                      ? "border-magenta bg-magenta-soft-bg text-text-primary"
                      : "border-default bg-surface-2 text-text-secondary hover:text-text-primary",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="font-sans text-xs font-medium text-text-secondary pt-1">Notas (las lee el bot)</div>
            <textarea
              className="w-full rounded-md border border-default bg-surface-2 px-3 py-2 font-sans text-sm text-text-primary"
              rows={3}
              value={notes ?? contact?.notes ?? ""}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: dueña de un local del mall; ya se le resolvió el reclamo del 3/11."
            />
            {notes !== null && notes !== (contact?.notes ?? "") && (
              <button
                type="button"
                className="rounded-md border border-default bg-surface-2 px-3 py-1 text-xs font-sans text-text-primary hover:bg-surface-1"
                onClick={() =>
                  update.mutate(
                    { key: { clientSlug, externalUserId: userId }, notes: notes.trim() || null },
                    { onSuccess: () => setNotes(null) },
                  )
                }
              >
                Guardar notas
              </button>
            )}
          </div>
          {p.conversationId && (
            <div className="px-5 pt-3">
              <Link
                to="/c/$clientSlug/inbox/$conversationId"
                params={{ clientSlug, conversationId: p.conversationId }}
                className="font-sans text-xs text-text-secondary hover:text-text-primary"
              >
                Abrir la conversación en el inbox →
              </Link>
            </div>
          )}
          <ul className="px-5 py-3 space-y-3">
            {data.messages.map((m) => (
              <li key={m.id} className="space-y-1">
                <div className="font-sans text-2xs text-text-muted">
                  {m.platform} · {m.kind === "comment" ? "comentario" : "dm"} · {formatArrivalAge(m.receivedAt)}
                  {m.outcome === "waiting" && ` · escalado${m.reason ? `: ${REASON_LABEL[m.reason] ?? m.reason}` : ""}`}
                </div>
                <p className="font-sans text-sm text-text-primary whitespace-pre-wrap">
                  {m.body || <span className="text-text-muted">({m.attachment ?? "sin texto"})</span>}
                </p>
                {m.response && <p className="font-sans text-sm text-text-secondary whitespace-pre-wrap">→ {m.response}</p>}
                {m.outcome === "waiting" && m.rationale && (
                  <p className="font-sans text-2xs text-state-escalated-text">{m.rationale}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

export function PeoplePage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug: string };
  const [days, setDays] = useState(90);
  const [state, setState] = useState<UserState | null>(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = usePeople(clientSlug, days);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase().replace(/^@/, "");
    return (data?.people ?? []).filter(
      (p: PersonProfile) =>
        (!state || p.flags.includes(state)) &&
        (!term || (p.username ?? p.userId).toLowerCase().includes(term) || p.lastMessage.body.toLowerCase().includes(term)),
    );
  }, [data, state, q]);

  const countFor = (s: UserState) => (data?.people ?? []).filter((p) => p.flags.includes(s)).length;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1200px] px-6 py-6 space-y-5">
        <div>
          <div className="font-mono text-2xs uppercase tracking-wider text-text-muted mb-1">{clientSlug} / personas</div>
          <h1 className="font-sans text-3xl font-semibold tracking-snug text-text-primary">Personas</h1>
          <p className="font-sans text-sm text-text-muted mt-1">
            Quién le escribe a la marca: cuánto, qué pasó con sus mensajes y cómo es. El estado sale del historial y de
            las etiquetas del equipo; las etiquetas y notas las lee el bot en los clientes donde el perfil está activo.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center rounded-full bg-surface-2 border border-default p-[3px]">
            {[30, 90, 180].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={cn(
                  "h-7 px-3.5 rounded-full text-xs font-sans font-medium transition-colors",
                  days === d ? "bg-surface-1 text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary",
                )}
              >
                {d} días
              </button>
            ))}
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar @usuario o palabra"
            className="h-8 w-64 rounded-full border border-default bg-surface-2 px-3.5 font-sans text-xs text-text-primary"
          />
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Personas", data.summary.people],
              ["Volvieron a escribir", data.summary.recurrent],
              ["Conflictivas o con reclamo", countFor("conflictive") + countFor("open_claim")],
              ["Puntaje promedio", data.summary.averageScore ?? "—"],
            ].map(([l, v]) => (
              <div key={l as string} className="rounded-xl border border-default bg-surface-1 p-4">
                <div className="font-sans text-2xl font-semibold tabular-nums text-text-primary">{v}</div>
                <div className="font-sans text-xs text-text-muted mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        )}

        {data && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setState(null)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-sans font-medium",
                !state ? "border-magenta bg-magenta-soft-bg text-text-primary" : "border-default bg-surface-2 text-text-secondary",
              )}
            >
              Todas <span className="font-mono text-2xs text-text-muted">{data.people.length}</span>
            </button>
            {ORDER.filter((s) => countFor(s) > 0).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setState(state === s ? null : s)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-sans font-medium",
                  state === s ? "border-magenta bg-magenta-soft-bg text-text-primary" : "border-default bg-surface-2 text-text-secondary hover:text-text-primary",
                )}
              >
                {STATE_LABEL[s]} <span className="font-mono text-2xs text-text-muted">{countFor(s)}</span>
              </button>
            ))}
          </div>
        )}

        <div className={cn("grid gap-5", selected ? "grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]" : "grid-cols-1")}>
          <section className="rounded-xl border border-default bg-surface-1 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-sm text-text-muted text-center">Armando los perfiles…</div>
            ) : shown.length === 0 ? (
              <div className="p-8 text-sm text-text-muted text-center">Nadie con ese filtro.</div>
            ) : (
              <ul>
                {shown.slice(0, 300).map((p) => (
                  <li key={p.userId} className="border-b border-subtle last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setSelected(p.userId)}
                      className={cn("w-full text-left px-5 py-3 hover:bg-surface-2 transition-colors", selected === p.userId && "bg-surface-2")}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-sans text-sm font-medium text-text-primary truncate">@{p.username ?? p.userId}</span>
                          <StateBadge s={p.state} />
                          {p.tags.filter((t) => !["spam", "vip", "influencer", "reclamo", "conflictivo"].includes(t)).map((t) => (
                            <span key={t} className="font-sans text-2xs text-text-muted">#{t}</span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className="font-sans text-2xs text-text-muted tabular-nums">
                            {p.messages} msj · {p.activeDays} días · {p.outcomes.waiting} esc.
                          </span>
                          <Score n={p.score} />
                        </div>
                      </div>
                      <p className="mt-1 font-sans text-xs text-text-muted line-clamp-1">
                        {formatArrivalAge(p.lastSeen)} · {p.lastMessage.body || `(${p.lastMessage.attachment ?? "sin texto"})`}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {selected && <Detail clientSlug={clientSlug} userId={selected} days={days} onClose={() => setSelected(null)} />}
        </div>
      </div>
    </div>
  );
}
